import React, { createContext, useContext, useState, useEffect } from 'react';
import emailService, { initializeEmailService } from '../services/emailService';
import { useAuth } from './AuthContext';

const EmailContext = createContext();

export const useEmail = () => {
  const context = useContext(EmailContext);
  if (!context) {
    throw new Error('useEmail must be used within an EmailProvider');
  }
  return context;
};

export const EmailProvider = ({ children }) => {
  const { user } = useAuth();
  const [emailQueue, setEmailQueue] = useState([]);
  const [emailSettings, setEmailSettings] = useState({
    serviceId: import.meta.env.VITE_EMAILJS_SERVICE_ID || '',
    publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY || '',
    notifications: {
      newReports: true,
      reportUpdates: true,
      investigations: true,
      deadlineReminders: true,
      monthlyReports: true,
      hmsIncidents: true,
      bookingNotifications: true,
      supportReceipts: true,
      partnerRegistration: true
    },
    frequency: {
      immediate: true,
      daily: false,
      weekly: false
    }
  });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    initializeEmailService();
    loadEmailSettings();
  }, []);

  const loadEmailSettings = () => {
    try {
      const saved = localStorage.getItem('hms_email_settings');
      if (saved) {
        const parsedSettings = JSON.parse(saved);
        setEmailSettings(parsedSettings);
        if (parsedSettings.serviceId && parsedSettings.publicKey) {
          emailService.updateConfiguration(parsedSettings.serviceId, parsedSettings.publicKey);
        }
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
    }
  };

  const saveEmailSettings = (newSettings) => {
    try {
      setEmailSettings(newSettings);
      localStorage.setItem('hms_email_settings', JSON.stringify(newSettings));
      if (newSettings.serviceId && newSettings.publicKey) {
        emailService.updateConfiguration(newSettings.serviceId, newSettings.publicKey);
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
    }
  };

  const queueEmail = (emailData) => {
    const emailItem = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      ...emailData
    };
    setEmailQueue(prev => [...prev, emailItem]);
    if (emailSettings.frequency.immediate) {
      processEmailQueue();
    }
  };

  const processEmailQueue = async () => {
    if (isProcessing || emailQueue.length === 0) return;
    setIsProcessing(true);
    try {
      await emailService.sendBatchEmails(emailQueue);
      setEmailQueue([]);
    } catch (error) {
      console.error('Error processing email queue:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const processQueueManually = () => {
    processEmailQueue();
  };

  const clearEmailQueue = () => {
    setEmailQueue([]);
  };

  // --- NYE METODER FOR REGISTRERING OG SUPPORT ---

  const sendCompanyRegistration = async (email, companyName, planName, adminName) => {
    console.log('📧 Queuing company registration email for:', companyName);
    queueEmail({
      type: 'companyRegistration',
      userEmail: email,
      templateId: 'template_company_registration',
      templateParams: {
        company_name: companyName,
        admin_name: adminName,
        subscription_plan: planName,
        login_url: `${window.location.origin}/#/company/login`
      }
    });
    return { success: true };
  };

  const sendSupportReceipt = async (email, formData) => {
    console.log('📧 Queuing support receipt for:', email);
    queueEmail({
      type: 'supportReceipt',
      userEmail: email,
      templateId: 'template_receipt', // Gjenbruker kvitteringsmal eller spesifikk support-mal
      templateParams: {
        user_email: email,
        subject: formData.subject,
        category: formData.category,
        message: formData.message,
        priority: formData.priority
      }
    });
    return { success: true };
  };

  const sendPartnerRegistrationNotification = async (partnerData) => {
    queueEmail({
      type: 'partnerRegistration',
      userEmail: 'admin@hmsvarsling.no',
      templateId: 'template_new_partner',
      templateParams: {
        partner_name: partnerData.name,
        partner_email: partnerData.email,
        partner_phone: partnerData.phone,
        partner_id: partnerData.partnerId
      }
    });

    queueEmail({
      type: 'partnerConfirmation',
      userEmail: partnerData.email,
      templateId: 'template_welcome',
      templateParams: {
        user_name: partnerData.contactPerson || partnerData.name,
        user_type: 'Partner',
        company_name: 'HMSVarsling'
      }
    });
    return { success: true };
  };

  const value = {
    emailSettings,
    saveEmailSettings,
    emailQueue,
    isProcessing,
    processQueueManually,
    clearEmailQueue,
    sendCompanyRegistration,
    sendSupportReceipt,
    sendPartnerRegistrationNotification,
    sendWelcomeEmail: (email, name, type, company) => 
      queueEmail({
        type: 'welcome',
        userEmail: email,
        templateId: 'template_welcome',
        templateParams: {
          user_name: name,
          user_type: type,
          company_name: company
        }
      }),
    sendPasswordResetEmail: (email, token, name) => 
      emailService.sendPasswordResetEmail(email, token, name)
  };

  return <EmailContext.Provider value={value}>{children}</EmailContext.Provider>;
};